/**
 * Element Events Utility
 * 
 * Provides simplified interfaces for working with element events.
 * This includes helper functions for broadcasting events with less boilerplate.
 */

import { broadcastElementEvent as originalBroadcastEvent } from './event-bridge';
import { BroadcastResult } from '../types';

/**
 * Context for an element's events
 */
export interface ElementEventContext {
  elementType: string;
  elementId: string;
  getEventName?: (eventName: string) => string;
}

/**
 * Creates a simplified event broadcaster for a specific element
 * 
 * @param context - The element event context (type, id, and name function)
 * @returns A function that can be used to broadcast events with minimal parameters
 */
export function createElementEventBroadcaster(context: ElementEventContext) {
  const { elementType, elementId } = context;
  
  /**
   * Broadcasts an event for the element with pre-configured context
   * 
   * @param eventName - The base name of the event (without prefix)
   * @param data - The data to include with the event
   * @returns Result with success status and any validation errors
   */
  return function broadcastEvent(
    eventName: string,
    data: Record<string, unknown> = {}
  ): BroadcastResult {
    // For schema registration, we need the prefixed name
    // but for event broadcasting, we want to use the unprefixed name
    // The event bridge will handle this internally
    return originalBroadcastEvent(eventName, elementType, elementId, data);
  };
}

/**
 * Creates a hook-friendly event context for React components
 * 
 * @param elementType - The type of the element
 * @param elementId - The ID of the element instance
 * @param getEventName - Function to get the full event name with prefix
 * @returns An object with the broadcast function and context
 */
export function createElementEvents(
  elementType: string,
  elementId: string,
  getEventName?: (eventName: string) => string
) {
  const context: ElementEventContext = {
    elementType,
    elementId,
    getEventName
  };
  
  const broadcast = createElementEventBroadcaster(context);
  
  return {
    broadcast,
    context
  };
}
